/* One person, six ways.
 *
 * The VIDE action log has 105 items belonging to Rosalinda Browne, spread
 * across "Rosalinda Browne / WOB", "Rosalinda Browne", "Rosalinda B.",
 * "Rosalinda B", "Browne / WOB" and "Rosalinda". Imported as they are, the
 * register reports six owners with a handful of items each and the question
 * "what is on Rosalinda's plate" has no answer.
 *
 * So names are grouped and a canonical form is proposed. Proposed, not
 * applied: two people really can share a surname, and quietly merging two
 * colleagues into one is worse than leaving six spellings alone. The import
 * preview shows each group with a tick.
 */

/* An organisation suffix is not part of a person's name. These are the ones
   that actually appear in these files. */
const ORGS = /\s*[/,(]\s*(wob|witt o'?brien'?s|dlr|vide|z2|itron|wapa|awg|smartworks|haugland|trident)\s*\)?\s*$/i;

export function tidyName(raw = "") {
  let name = String(raw).replace(/\s+/g, " ").trim();
  /* Repeatedly, because "Ashley Bryan / WOB / DLR" carries two of them and
     stripping the last one only leaves a slash behind that then reads as a
     second owner. */
  for (let i = 0; i < 4; i += 1) {
    const shorter = name.replace(ORGS, "").trim();
    if (shorter === name || !shorter) break;
    name = shorter;
  }
  return name;
}

/* Some cells hold several owners: "Rosalinda Browne / David Addo / Shannon
   Baxley", "VIDE & WOB", "Rosalinda B. and Heidi W.". Those are shared items,
   not spellings of one person, and they are poison to the grouping: every
   name in such a cell matches it, so one of them bridges three separate
   people into a single group. In the real VIDE log that produced a group of
   107 rows spanning three people, and swallowed the 43 items owned by WOB
   into a five-name string.
   So a value naming more than one owner is left entirely alone. It gets its
   own group, it is never a canonical, and nothing merges through it. */
export function isShared(raw = "") {
  /* Any separator at all, spaced or not. The real file has "Rosalinda Browne;
     Victor Hall; MCN; VIDE", "WOB/SMEs Sahil/Len/Ben" and "Rosalinda B. and
     Heidi W.", and a rule that only caught spaced slashes let every one of
     them bridge three people into one group. */
  return /[;/&,]|\band\b/i.test(tidyName(raw));
}

/* The pieces of a name that identify it: a first name, and a surname which
   may have been shortened to an initial. Case and punctuation go. */
function parts(raw = "") {
  return tidyName(raw)
    .toLowerCase()
    .replace(/[.,]/g, "")
    .split(/[\s/]+/)
    .filter(Boolean);
}

/* Two names are the same person when every token of one is a prefix of a
   token of the other, in order, and at least one token matches in full.
   "rosalinda b" fits "rosalinda browne" and matches rosalinda whole.
   "browne" alone matches on the surname in full. "lana bayless" fits nothing
   of hers. The full-token requirement is what stops "r b" merging with
   everybody who happens to share two initials.
   Both directions are tried. Length is no guide to which name is the fuller
   one: "Rosalinda B." and "Rosalinda Browne" are both two tokens, and testing
   only one way round misses the pair entirely. */
function fits(short, long) {
  let at = 0;
  let exact = 0;
  for (const token of short) {
    const found = long.findIndex((t, i) => i >= at && t.startsWith(token));
    if (found === -1) return false;
    if (long[found] === token) exact += 1;
    at = found + 1;
  }
  return exact > 0;
}

export function samePerson(a, b) {
  const [x, y] = [parts(a), parts(b)];
  if (!x.length || !y.length) return false;
  return fits(x, y) || fits(y, x);
}

/* Group the names in a column, proposing the fullest spelling of each as the
   one to keep. Fullest rather than commonest: "Rosalinda Browne" is the name,
   even if "Rosalinda B." was typed more often. */
export function groupNames(values = []) {
  const counts = new Map();
  for (const value of values) {
    const name = tidyName(value);
    if (!name) continue;
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }

  const groups = [];
  for (const [name, count] of [...counts.entries()].sort((a, b) => b[0].length - a[0].length)) {
    if (isShared(name)) {
      groups.push({ canonical: name, count, variants: [{ name, count }], shared: true });
      continue;
    }
    const hit = groups.find((g) => !g.shared && g.variants.some((v) => samePerson(v.name, name)));
    if (hit) {
      hit.variants.push({ name, count });
      hit.count += count;
    } else {
      groups.push({ canonical: name, count, variants: [{ name, count }] });
    }
  }

  return groups
    .map((g) => ({
      ...g,
      variants: g.variants.sort((a, b) => b.count - a.count),
    }))
    .sort((a, b) => b.count - a.count);
}

/* The rename map for the groups somebody has agreed to.
 *
 * Every variant maps to the canonical, the canonical included. That last part
 * matters: "Rosalinda Browne / WOB" tidies to exactly the canonical name, so
 * without an entry for it the row would keep its "/ WOB" while the row next to
 * it lost one, and the register would show the same person twice. Accepting a
 * group normalises every row in it or it is not worth accepting. */
export function renameMap(groups = [], accepted = null) {
  const out = new Map();
  for (const g of groups) {
    if (g.variants.length < 2) continue;
    if (accepted && !accepted.has(g.canonical)) continue;
    for (const v of g.variants) out.set(v.name, g.canonical);
  }
  return out;
}

/* Applies only what was agreed to, and nothing else.
 *
 * It is tempting to also strip "/ WOB" off every name on the way past, since
 * the grouping did that to compare them. Resisted: a name nobody ticked comes
 * through exactly as the workbook spelled it. An importer that changes a value
 * somebody did not approve is an importer nobody can trust with 389 rows. */
export function applyNames(rows = [], field, map) {
  if (!map?.size) return rows;
  return rows.map((r) => {
    const to = map.get(tidyName(r[field]));
    return to ? { ...r, [field]: to } : r;
  });
}
